import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { t } from "./i18n";
import { TOS_VERSION } from "./legal";

const SUPABASE_URL: string | undefined = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY: string | undefined = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const R2_PUBLIC_BASE: string | undefined = import.meta.env.VITE_R2_PUBLIC_BASE;

export type SceneSort = "play_count" | "like_count" | "created_at";

/**
 * A Community / self-post listing query. `account` ilike-matches the
 * denormalized owner_name (author search is by name only; the owner UUID is
 * never accepted or shown). `sinceDays` filters by created_at (null = no time
 * bound). `official` true lists only the tutorial/official scenes; false
 * (default) lists only user uploads. `mine` switches to the signed-in user's
 * own posts (any status).
 */
export type SceneQuery = {
  text?: string;
  account?: string;
  sort: SceneSort;
  sinceDays?: number | null;
  official?: boolean;
  mine?: boolean;
  tags?: string[];
  autoInclude?: string[];
  autoExclude?: string[];
};

export type SceneListItem = {
  id: string;
  owner: string;
  ownerName: string | null;
  title: string;
  playCount: number;
  likeCount: number;
  bookmarkCount: number;
  status: string;
  tags: string[];
  autoTags: string[];
  lockedTags: string[];
};

export type UploadResult =
  | { kind: "ok"; id: string }
  | { kind: "reject"; reason: string }
  | { kind: "needs-signin" }
  | { kind: "throttled"; waitSeconds: number; reason: string }
  | { kind: "blocked" }
  | { kind: "timeout" }
  | { kind: "error"; message: string };

export const UPLOAD_TIMEOUT_MS = 30_000;

export type AuthStatus = { signedIn: boolean; email: string | null; name: string | null };

let cachedClient: SupabaseClient | null = null;

function client(): SupabaseClient | null {
  if (cachedClient !== null) {
    return cachedClient;
  }
  if (SUPABASE_URL === undefined || SUPABASE_PUBLISHABLE_KEY === undefined) {
    return null;
  }
  cachedClient = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: { persistSession: true, autoRefreshToken: true },
  });
  return cachedClient;
}

export function shareEnabled(): boolean {
  return SUPABASE_URL !== undefined && SUPABASE_PUBLISHABLE_KEY !== undefined;
}

/**
 * Anonymous sign-in is created lazily so that viewers who only open a shared URL
 * never get an auth session. Upload / report / delete call this first because
 * they need a uid for ownership, rate limiting, and one-vote-per-user.
 */
async function ensureAuth(c: SupabaseClient): Promise<string | null> {
  const { data } = await c.auth.getSession();
  if (data.session !== null) {
    return null;
  }
  const { error } = await c.auth.signInAnonymously();
  return error === null ? null : error.message;
}

async function currentUid(c: SupabaseClient): Promise<string | null> {
  const { data } = await c.auth.getSession();
  return data.session === null ? null : data.session.user.id;
}

/**
 * Uploading requires a real (non-anonymous) sign-in so per-uid rate limits and
 * ownership cannot be bypassed by rotating anonymous uids. Anonymous sessions
 * (used for like/report viewers) do not count.
 */
async function hasUploaderSession(c: SupabaseClient): Promise<boolean> {
  const { data } = await c.auth.getSession();
  return data.session !== null && data.session.user.is_anonymous !== true;
}

/** Cheap sign-in classification for telemetry (no profile fetch, unlike authStatus). */
export async function authKind(): Promise<"none" | "anon" | "real"> {
  const c = client();
  if (c === null) {
    return "none";
  }
  const { data } = await c.auth.getSession();
  if (data.session === null) {
    return "none";
  }
  return data.session.user.is_anonymous === true ? "anon" : "real";
}

/** The R2 object key for a recording: content-addressable under the owner so each owner's copy is deleted independently. */
export function micStoragePath(owner: string, hash: string): string {
  return `mic/${owner}/${hash}`;
}

/** The public R2 URL for a stored recording key, or null when VITE_R2_PUBLIC_BASE is not configured (no R2 backend → imports stay silent). */
export function micPublicUrl(storagePath: string): string | null {
  if (R2_PUBLIC_BASE === undefined) {
    return null;
  }
  return `${R2_PUBLIC_BASE.replace(/\/+$/, "")}/${storagePath}`;
}

export function r2Configured(): boolean {
  return R2_PUBLIC_BASE !== undefined;
}

export async function uploadScene(title: string, scene: unknown, signal?: AbortSignal): Promise<UploadResult> {
  const c = client();
  if (c === null) {
    return { kind: "error", message: t("share.notConfigured") };
  }
  if (!(await hasUploaderSession(c))) {
    return { kind: "needs-signin" };
  }
  const { data, error } = await c.functions.invoke("upload-scene", {
    body: { title, scene, tosVersion: TOS_VERSION },
    signal,
  });
  if (error !== null) {
    return { kind: "error", message: error.message };
  }
  const res = data as { status?: string; id?: string; reason?: string; waitSeconds?: number };
  if (res.status === "ok" && typeof res.id === "string") {
    return { kind: "ok", id: res.id };
  }
  if (res.status === "blocked") {
    return { kind: "blocked" };
  }
  if (res.status === "reject") {
    return { kind: "reject", reason: typeof res.reason === "string" ? res.reason : t("share.rejected") };
  }
  if (res.status === "throttled") {
    return {
      kind: "throttled",
      waitSeconds: typeof res.waitSeconds === "number" ? res.waitSeconds : 0,
      reason: typeof res.reason === "string" ? res.reason : t("share.throttled"),
    };
  }
  return { kind: "error", message: t("share.unexpectedResponse") };
}

/** Per-owner cloud mic quota, mirrored from the upload-mic Edge Function for the pre-upload remaining display. */
export const MIC_MAX_COUNT = 32;
export const MIC_MAX_BYTES = 8 * 1024 * 1024;

export type MicAssetInput = { hash: string; mime: string; durationMs: number; name: string; blob: Blob };

export type MicUploadResult =
  | { kind: "ok"; available: string[] }
  | { kind: "quota"; used: { count: number; bytes: number }; adding: { count: number; bytes: number }; max: { count: number; bytes: number } }
  | { kind: "needs-signin" }
  | { kind: "error"; message: string };

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** The content hashes the signed-in user already has in the cloud, so the upload path can skip re-sending bytes already stored. Empty on any error (the Edge Function re-dedups authoritatively). */
export async function listMicHashes(): Promise<Set<string>> {
  const c = client();
  if (c === null) {
    return new Set<string>();
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return new Set<string>();
  }
  const { data, error } = await c.from("mic_assets").select("hash").eq("owner", uid);
  if (error !== null) {
    return new Set<string>();
  }
  return new Set<string>(((data ?? []) as { hash: string }[]).map((r: { hash: string }): string => r.hash));
}

/** The signed-in user's current cloud mic usage (count + total bytes), or null when not signed in / unavailable. */
export async function micUsage(): Promise<{ count: number; bytes: number } | null> {
  const c = client();
  if (c === null) {
    return null;
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return null;
  }
  const { data, error } = await c.from("mic_assets").select("byte_size").eq("owner", uid);
  if (error !== null) {
    return null;
  }
  const rows = (data ?? []) as { byte_size: number }[];
  return { count: rows.length, bytes: rows.reduce((sum: number, r: { byte_size: number }): number => sum + r.byte_size, 0) };
}

/**
 * Uploads mic recordings the signed-in user does not yet have in the cloud (each
 * sent as inline base64), enforcing the per-owner quota server-side. A quota
 * result reports current usage so the caller can block the post and explain why.
 */
export async function uploadMicAssets(assets: MicAssetInput[], signal?: AbortSignal): Promise<MicUploadResult> {
  const c = client();
  if (c === null) {
    return { kind: "error", message: t("share.notConfigured") };
  }
  if (!(await hasUploaderSession(c))) {
    return { kind: "needs-signin" };
  }
  const payload = await Promise.all(
    assets.map(async (a: MicAssetInput): Promise<Record<string, unknown>> => ({
      hash: a.hash,
      mime: a.mime,
      byteSize: a.blob.size,
      durationMs: a.durationMs,
      name: a.name,
      dataBase64: await blobToBase64(a.blob),
    })),
  );
  const { data, error } = await c.functions.invoke("upload-mic", { body: { assets: payload }, signal });
  if (error !== null) {
    return { kind: "error", message: error.message };
  }
  const res = data as {
    status?: string;
    available?: string[];
    used?: { count: number; bytes: number };
    adding?: { count: number; bytes: number };
    max?: { count: number; bytes: number };
  };
  if (res.status === "ok") {
    return { kind: "ok", available: Array.isArray(res.available) ? res.available : [] };
  }
  if (res.status === "quota" && res.used !== undefined && res.adding !== undefined && res.max !== undefined) {
    return { kind: "quota", used: res.used, adding: res.adding, max: res.max };
  }
  return { kind: "error", message: t("share.unexpectedResponse") };
}

export type MicAsset = { hash: string; name: string; byteSize: number; durationMs: number; sceneCount: number };

/** The signed-in user's uploaded mic recordings with a per-recording count of how many of their posted scenes use it (for the delete warning). */
export async function listMicAssets(): Promise<{ items: MicAsset[] } | { error: string }> {
  const c = client();
  if (c === null) {
    return { error: t("share.notConfigured") };
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return { items: [] };
  }
  const { data: assets, error } = await c
    .from("mic_assets")
    .select("hash,name,byte_size,duration_ms")
    .eq("owner", uid)
    .order("created_at", { ascending: true });
  if (error !== null) {
    return { error: error.message };
  }
  const { data: links } = await c.from("scene_mic_assets").select("hash").eq("owner", uid);
  const counts = new Map<string, number>();
  for (const l of (links ?? []) as { hash: string }[]) {
    counts.set(l.hash, (counts.get(l.hash) ?? 0) + 1);
  }
  type Row = { hash: string; name: string; byte_size: number; duration_ms: number };
  const items = ((assets ?? []) as Row[]).map((r: Row): MicAsset => ({
    hash: r.hash,
    name: r.name,
    byteSize: r.byte_size,
    durationMs: r.duration_ms,
    sceneCount: counts.get(r.hash) ?? 0,
  }));
  return { items };
}

/** Hard-deletes an uploaded recording: the R2 object, its mic_assets row, and its scene links, via the delete-mic Edge Function. */
export async function deleteMicAsset(hash: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  if (!(await hasUploaderSession(c))) {
    return t("share.notSignedIn");
  }
  const { data, error } = await c.functions.invoke("delete-mic", { body: { hash } });
  if (error !== null) {
    return error.message;
  }
  return (data as { status?: string }).status === "ok" ? null : t("share.unexpectedResponse");
}

export async function myUid(): Promise<string | null> {
  const c = client();
  if (c === null) {
    return null;
  }
  return currentUid(c);
}

/**
 * Ensures a session (anonymous if none) and returns its uid, for joining or
 * creating a room. Room presence, edit claims, color assignment, and owner
 * enforcement all key on a stable per-participant uid, so — unlike a passive
 * viewer — every room participant must hold a session before the hello is sent;
 * without it the uid is empty and every sessionless participant collides on "".
 * Returns null only when share is unconfigured or anonymous sign-in fails.
 */
export async function ensureRoomUid(): Promise<string | null> {
  const c = client();
  if (c === null) {
    return null;
  }
  await ensureAuth(c);
  return currentUid(c);
}

/**
 * The current session's access token (JWT), carried in the room hello so the room
 * DO can verify the joiner server-side against /auth/v1/user. Anonymous and real
 * sessions both have one; null only when there is no session / share is
 * unconfigured. No alternate-path lookup — a missing token surfaces as null so
 * the gate fails closed.
 */
export async function myAccessToken(): Promise<string | null> {
  const c = client();
  if (c === null) {
    return null;
  }
  const { data } = await c.auth.getSession();
  return data.session === null ? null : data.session.access_token;
}

/** True when the current session is a real (non-anonymous) signed-in account, used to disable the anon-gate toggle for anonymous creators (self-lockout prevention). */
export async function isRealAccount(): Promise<boolean> {
  const c = client();
  if (c === null) {
    return false;
  }
  return hasUploaderSession(c);
}

/**
 * True when linkIdentity failed because the Google identity is already bound to
 * another account or manual linking is unavailable — the cases where falling
 * back to a plain sign-in is correct. SDK error codes vary by version, so both
 * the code and the message are inspected.
 */
function isIdentityLinkConflict(error: { code?: string; message: string }): boolean {
  const code = error.code ?? "";
  const msg = error.message.toLowerCase();
  return (
    code === "identity_already_exists" ||
    code === "manual_linking_disabled" ||
    msg.includes("already") ||
    msg.includes("manual linking")
  );
}

/**
 * Promotes an anonymous session in place: linkIdentity keeps the same uid (so
 * existing likes/bookmarks carry over) while flipping is_anonymous to false. The
 * fallback to signInWithOAuth is an API-compatible branch, not an
 * alternate-path lookup — it runs when the Google identity is already bound to
 * another account or manual linking is off, leaving the orphaned anonymous row
 * for the purge job. A non-anonymous or absent session uses plain OAuth sign-in.
 */
export async function signInWithGoogle(): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const redirectTo = window.location.origin + window.location.pathname;
  const { data } = await c.auth.getSession();
  const session = data.session;
  if (session !== null && session.user.is_anonymous === true) {
    const { error } = await c.auth.linkIdentity({ provider: "google", options: { redirectTo } });
    if (error === null) {
      return null;
    }
    if (!isIdentityLinkConflict(error)) {
      return error.message;
    }
  }
  const { error } = await c.auth.signInWithOAuth({ provider: "google", options: { redirectTo } });
  return error === null ? null : error.message;
}

export async function signOut(): Promise<void> {
  const c = client();
  if (c === null) {
    return;
  }
  await c.auth.signOut();
}

export const DISPLAY_NAME_MAX = 32;
export const DISPLAY_NAME_MIN = 3;

function randomDigits(count: number): string {
  let out = "";
  for (let i = 0; i < count; i++) {
    out += Math.floor(Math.random() * 10);
  }
  return out;
}

/** First three characters of the email local part, an underscore, then 12 random digits. */
function defaultDisplayName(email: string | null): string {
  const head = (email ?? "usr").slice(0, 3);
  return `${head}_${randomDigits(12)}`;
}

/**
 * The display name is the editable account name held in the profiles table (the
 * uniqueness authority). When a freshly signed-in user has no profile row yet, a
 * default (email head + random digits) is generated and persisted once via
 * set_display_name so the name is stable and editable thereafter.
 */
export async function authStatus(): Promise<AuthStatus> {
  const c = client();
  if (c === null) {
    return { signedIn: false, email: null, name: null };
  }
  const { data } = await c.auth.getSession();
  const session = data.session;
  if (session === null || session.user.is_anonymous === true) {
    return { signedIn: false, email: null, name: null };
  }
  const email = session.user.email ?? null;
  const { data: profile } = await c.from("profiles").select("display_name").eq("owner", session.user.id).maybeSingle();
  const stored = profile === null ? null : (profile as { display_name: string }).display_name;
  if (stored !== null) {
    return { signedIn: true, email, name: stored };
  }
  const generated = defaultDisplayName(email);
  await c.rpc("set_display_name", { p_name: generated });
  return { signedIn: true, email, name: generated };
}

/**
 * Persists the editable display name through set_display_name (the
 * case-insensitive uniqueness gate; a collision returns the "name in use"
 * message) and then propagates it onto the caller's already-uploaded scenes
 * (rename_owner_scenes), so the Community author label always reflects the
 * current name. profiles is the single source of truth for the name.
 */
export async function setDisplayName(name: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const trimmed = name.trim().slice(0, DISPLAY_NAME_MAX);
  if (trimmed === "") {
    return t("account.nameEmpty");
  }
  if (trimmed.length < DISPLAY_NAME_MIN) {
    return t("account.nameTooShort", { min: String(DISPLAY_NAME_MIN) });
  }
  const { error } = await c.rpc("set_display_name", { p_name: trimmed });
  if (error !== null) {
    return error.code === "23505" ? t("account.nameTaken") : error.message;
  }
  const { error: renameError } = await c.rpc("rename_owner_scenes", { new_name: trimmed });
  return renameError === null ? null : renameError.message;
}

export type SceneTagOps = { adds: string[]; removes: string[]; locks: string[]; unlocks: string[] };
export type TagMutationResult = { tags: string[]; lockedTags: string[] } | { error: string };

/**
 * Flushes one scene's queued tag edits as add/remove/lock/unlock diffs through a
 * single batched, row-locked security-definer RPC: add/remove are open to any
 * real account, lock/unlock are owner-only, and the anonymous gate is enforced
 * server-side. Returns the resulting tag arrays so the caller can reconcile.
 * A real (non-anonymous) session is required, matching the server gate.
 */
export async function applySceneTagOps(sceneId: string, ops: SceneTagOps): Promise<TagMutationResult> {
  const c = client();
  if (c === null) {
    return { error: t("share.notConfigured") };
  }
  if (!(await hasUploaderSession(c))) {
    return { error: t("share.notSignedIn") };
  }
  const { data, error } = await c.rpc("apply_scene_tag_ops", {
    scene_id: sceneId,
    adds: ops.adds,
    removes: ops.removes,
    locks: ops.locks,
    unlocks: ops.unlocks,
  });
  if (error !== null) {
    return { error: error.message };
  }
  const res = (data ?? {}) as { tags?: unknown; locked_tags?: unknown };
  return {
    tags: Array.isArray(res.tags) ? (res.tags as string[]) : [],
    lockedTags: Array.isArray(res.locked_tags) ? (res.locked_tags as string[]) : [],
  };
}

export async function fetchScene(id: string): Promise<{ scene: unknown } | { error: string }> {
  const c = client();
  if (c === null) {
    return { error: t("share.notConfigured") };
  }
  const { data, error } = await c.from("scenes").select("scene").eq("id", id).maybeSingle();
  if (error !== null) {
    return { error: error.message };
  }
  if (data === null) {
    return { error: "scene not found" };
  }
  return { scene: (data as { scene: unknown }).scene };
}

export async function listScenes(q: SceneQuery): Promise<{ items: SceneListItem[] } | { error: string }> {
  const c = client();
  if (c === null) {
    return { error: t("share.notConfigured") };
  }
  const columns = "id,owner,owner_name,title,play_count,like_count,bookmark_count,status,tags,auto_tags,locked_tags";
  type Row = {
    id: string;
    owner: string;
    owner_name: string | null;
    title: string;
    play_count: number;
    like_count: number;
    bookmark_count: number;
    status: string;
    tags: string[] | null;
    auto_tags: string[] | null;
    locked_tags: string[] | null;
  };
  let filter = c.from("scenes").select(columns);
  if (q.mine === true) {
    const uid = await currentUid(c);
    if (uid === null) {
      return { items: [] };
    }
    filter = filter.eq("owner", uid);
  } else {
    filter = filter.eq("status", "active").eq("kind", q.official === true ? "official" : "user");
  }
  const text = q.text?.trim() ?? "";
  if (text !== "") {
    filter = filter.ilike("title", `%${text}%`);
  }
  const account = q.account?.trim() ?? "";
  if (account !== "") {
    filter = filter.ilike("owner_name", `%${account}%`);
  }
  const tags = (q.tags ?? []).map((tg: string): string => tg.trim()).filter((tg: string): boolean => tg !== "");
  if (tags.length > 0) {
    filter = filter.overlaps("tags", tags);
  }
  for (const tag of q.autoInclude ?? []) {
    filter = filter.contains("auto_tags", [tag]);
  }
  for (const tag of q.autoExclude ?? []) {
    filter = filter.not("auto_tags", "cs", `{${tag}}`);
  }
  if (q.sinceDays !== undefined && q.sinceDays !== null) {
    const since = new Date(Date.now() - q.sinceDays * 86_400_000).toISOString();
    filter = filter.gte("created_at", since);
  }
  const ordered =
    q.mine === true || q.sort === "created_at"
      ? filter.order("created_at", { ascending: false })
      : filter.order(q.sort, { ascending: false }).order("created_at", { ascending: false });
  const { data, error } = await ordered.limit(60);
  if (error !== null) {
    return { error: error.message };
  }
  const rows = (data ?? []) as Row[];
  const items = rows.map((r: Row): SceneListItem => ({
    id: r.id,
    owner: r.owner,
    ownerName: r.owner_name,
    title: r.title,
    playCount: r.play_count,
    likeCount: r.like_count,
    bookmarkCount: r.bookmark_count,
    status: r.status,
    tags: r.tags ?? [],
    autoTags: r.auto_tags ?? [],
    lockedTags: r.locked_tags ?? [],
  }));
  return { items };
}

export async function incrementPlay(id: string): Promise<void> {
  const c = client();
  if (c === null) {
    return;
  }
  await c.rpc("increment_play", { scene_id: id });
}

const playQueue = new Set<string>();

function playedKey(id: string): string {
  return `resonella.played.${id}`;
}

/**
 * Queues a scene to be counted as played, deferring the write until flushPlays.
 * The sessionStorage guard (shared with the `?scene=` immediate path) keeps one
 * count per scene per session, so a scene already counted by either path is not
 * re-queued. Setting the key here means "this session will count it" — flush
 * failures lose the count silently, which is acceptable for a soft metric.
 */
export function queuePlay(id: string): void {
  const key = playedKey(id);
  if (sessionStorage.getItem(key) !== null) {
    return;
  }
  sessionStorage.setItem(key, "1");
  playQueue.add(id);
}

/**
 * Sends all queued play ids in one increment_plays RPC and empties the queue.
 * Called on feed close and on pagehide/visibilitychange:hidden, so it must reach
 * the server during unload: a direct keepalive fetch is used because supabase-js
 * rpc does not expose keepalive. Only the apikey header is sent (no Authorization
 * Bearer): the publishable key may not be used as a Bearer token, and with no user
 * token PostgREST runs as anon — increment_plays is security definer granted to
 * anon, so the send stays synchronous (no async getSession before unload).
 */
export function flushPlays(): void {
  if (playQueue.size === 0 || SUPABASE_URL === undefined || SUPABASE_PUBLISHABLE_KEY === undefined) {
    return;
  }
  const ids = Array.from(playQueue);
  playQueue.clear();
  void fetch(`${SUPABASE_URL}/rest/v1/rpc/increment_plays`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({ ids }),
  });
}

/**
 * Reports a scene with a category reason. A unique-violation (23505) means the user already
 * reported this scene — treated as success so the action is idempotent. P0001 is raised by the
 * per-reporter rate-limit trigger and surfaced as a friendly "too many reports" message.
 */
export async function reportScene(id: string, reason: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const authErr = await ensureAuth(c);
  if (authErr !== null) {
    return authErr;
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return t("share.notSignedIn");
  }
  const { error } = await c.from("reports").insert({ scene_id: id, reporter: uid, reason });
  if (error === null || error.code === "23505") {
    return null;
  }
  if (error.code === "P0001") {
    return t("report.rateLimited");
  }
  return error.message;
}

export async function submitFeedback(body: string, scene?: unknown): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  if (!(await hasUploaderSession(c))) {
    return t("feedback.signInRequired");
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return t("feedback.signInRequired");
  }
  const trimmed = body.trim();
  if (trimmed === "") {
    return t("feedback.empty");
  }
  const payload: { sender: string; body: string; scene?: unknown } = { sender: uid, body: trimmed };
  if (scene !== undefined) {
    payload.scene = scene;
  }
  const { error } = await c.from("feedback").insert(payload);
  if (error === null) {
    return null;
  }
  if (error.code === "P0001") {
    return t("feedback.rateLimited");
  }
  return error.message || t("feedback.error");
}

/**
 * Likes a scene. A unique-violation (Postgres 23505) means the row already
 * exists — the user had already liked it — so it is treated as success rather
 * than surfaced as an error, keeping the action idempotent under stale UI state.
 */
export async function likeScene(id: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const authErr = await ensureAuth(c);
  if (authErr !== null) {
    return authErr;
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return t("share.notSignedIn");
  }
  const { error } = await c.from("likes").insert({ scene_id: id, liker: uid });
  if (error !== null && error.code !== "23505") {
    return error.message;
  }
  return null;
}

export async function unlikeScene(id: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return null;
  }
  const { error } = await c.from("likes").delete().eq("scene_id", id).eq("liker", uid);
  return error === null ? null : error.message;
}

export async function hasLiked(id: string): Promise<boolean> {
  const c = client();
  if (c === null) {
    return false;
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return false;
  }
  const { data } = await c.from("likes").select("scene_id").eq("scene_id", id).eq("liker", uid).maybeSingle();
  return data !== null;
}

/** The subset of the given scene ids the signed-in user has liked, fetched in one query for a feed listing. */
export async function likedSceneIds(ids: string[]): Promise<string[]> {
  const c = client();
  if (c === null || ids.length === 0) {
    return [];
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return [];
  }
  const { data } = await c.from("likes").select("scene_id").eq("liker", uid).in("scene_id", ids);
  return ((data ?? []) as { scene_id: string }[]).map((r: { scene_id: string }): string => r.scene_id);
}

export async function deleteScene(id: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return t("share.notSignedIn");
  }
  const { error } = await c.from("scenes").delete().eq("id", id);
  return error === null ? null : error.message;
}

export type BookmarkItem = SceneListItem & { categoryId: string | null; categoryName: string | null };

export type BookmarkCategory = { id: string; name: string };

/** The subset of the given scene ids the signed-in user has bookmarked, fetched in one query for a feed listing. */
export async function bookmarkedSceneIds(ids: string[]): Promise<string[]> {
  const c = client();
  if (c === null || ids.length === 0) {
    return [];
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return [];
  }
  const { data } = await c.from("bookmarks").select("scene_id").eq("owner", uid).in("scene_id", ids);
  return ((data ?? []) as { scene_id: string }[]).map((r: { scene_id: string }): string => r.scene_id);
}

/**
 * Bookmarks a scene under the given category (null = uncategorised). A
 * unique-violation (23505) means it is already bookmarked, treated as success so
 * the toggle is idempotent under stale UI state; the existing category is kept.
 */
export async function addBookmark(sceneId: string, categoryId: string | null): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const authErr = await ensureAuth(c);
  if (authErr !== null) {
    return authErr;
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return t("share.notSignedIn");
  }
  const { error } = await c.from("bookmarks").insert({ owner: uid, scene_id: sceneId, category_id: categoryId });
  if (error !== null && error.code !== "23505") {
    return error.message;
  }
  return null;
}

export async function removeBookmark(sceneId: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return null;
  }
  const { error } = await c.from("bookmarks").delete().eq("owner", uid).eq("scene_id", sceneId);
  return error === null ? null : error.message;
}

export async function setBookmarkCategory(sceneId: string, categoryId: string | null): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return t("share.notSignedIn");
  }
  const { error } = await c.from("bookmarks").update({ category_id: categoryId }).eq("owner", uid).eq("scene_id", sceneId);
  return error === null ? null : error.message;
}

/** Lists the signed-in user's bookmarks joined with their scene display metadata and category name. */
export async function listBookmarks(): Promise<{ items: BookmarkItem[] } | { error: string }> {
  const c = client();
  if (c === null) {
    return { error: t("share.notConfigured") };
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return { items: [] };
  }
  const columns =
    "scene_id,category_id,bookmark_categories(name),scenes(id,owner,owner_name,title,play_count,like_count,bookmark_count,status,tags,auto_tags,locked_tags)";
  type Row = {
    scene_id: string;
    category_id: string | null;
    bookmark_categories: { name: string } | null;
    scenes: {
      id: string;
      owner: string;
      owner_name: string | null;
      title: string;
      play_count: number;
      like_count: number;
      bookmark_count: number;
      status: string;
      tags: string[] | null;
      auto_tags: string[] | null;
      locked_tags: string[] | null;
    } | null;
  };
  const { data, error } = await c.from("bookmarks").select(columns).eq("owner", uid).order("created_at", { ascending: false }).returns<Row[]>();
  if (error !== null) {
    return { error: error.message };
  }
  const items: BookmarkItem[] = (data ?? [])
    .filter((r: Row): boolean => r.scenes !== null)
    .map((r: Row): BookmarkItem => {
      const s = r.scenes as NonNullable<Row["scenes"]>;
      return {
        id: s.id,
        owner: s.owner,
        ownerName: s.owner_name,
        title: s.title,
        playCount: s.play_count,
        likeCount: s.like_count,
        bookmarkCount: s.bookmark_count,
        status: s.status,
        tags: s.tags ?? [],
        autoTags: s.auto_tags ?? [],
        lockedTags: s.locked_tags ?? [],
        categoryId: r.category_id,
        categoryName: r.bookmark_categories?.name ?? null,
      };
    });
  return { items };
}

export async function listCategories(): Promise<{ items: BookmarkCategory[] } | { error: string }> {
  const c = client();
  if (c === null) {
    return { error: t("share.notConfigured") };
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return { items: [] };
  }
  const { data, error } = await c.from("bookmark_categories").select("id,name").eq("owner", uid).order("created_at", { ascending: true });
  if (error !== null) {
    return { error: error.message };
  }
  return { items: (data ?? []) as BookmarkCategory[] };
}

export async function addCategory(name: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const trimmed = name.trim();
  if (trimmed === "") {
    return t("account.nameEmpty");
  }
  const authErr = await ensureAuth(c);
  if (authErr !== null) {
    return authErr;
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return t("share.notSignedIn");
  }
  const { error } = await c.from("bookmark_categories").insert({ owner: uid, name: trimmed });
  return error === null ? null : error.message;
}

export async function renameCategory(id: string, name: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const trimmed = name.trim();
  if (trimmed === "") {
    return t("account.nameEmpty");
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return t("share.notSignedIn");
  }
  const { error } = await c.from("bookmark_categories").update({ name: trimmed }).eq("owner", uid).eq("id", id);
  return error === null ? null : error.message;
}

export async function deleteCategory(id: string): Promise<string | null> {
  const c = client();
  if (c === null) {
    return t("share.notConfigured");
  }
  const uid = await currentUid(c);
  if (uid === null) {
    return t("share.notSignedIn");
  }
  const { error } = await c.from("bookmark_categories").delete().eq("owner", uid).eq("id", id);
  return error === null ? null : error.message;
}
